import { i18n } from '@/i18n';

import {
	GET_NAVIGATION_MENU_LIST_REQUEST,
	GET_NAVIGATION_MENU_LIST_SUCCESS,
	GET_NAVIGATION_MENU_LIST_FAILURE,
	GET_NAVIGATION_MENU_REQUEST,
	GET_NAVIGATION_MENU_SUCCESS,
	GET_NAVIGATION_MENU_FAILURE,
	CREATE_NAVIGATION_MENU_REQUEST,
	CREATE_NAVIGATION_MENU_SUCCESS,
	CREATE_NAVIGATION_MENU_FAILURE,
	UPDATE_NAVIGATION_MENU_REQUEST,
	UPDATE_NAVIGATION_MENU_SUCCESS,
	UPDATE_NAVIGATION_MENU_FAILURE,
	DELETE_NAVIGATION_MENU_REQUEST,
	DELETE_NAVIGATION_MENU_SUCCESS,
	DELETE_NAVIGATION_MENU_FAILURE,
} from '../types';

import {
	getNavigationMenusAPI,
	getNavigationMenuAPI,
	postNavigationMenuAPI,
	updateNavigationMenuAPI,
	deleteNavigationMenuAPI,
} from '../../services/api/navigationMenu.api';

import {
	transformedNavigationMenuItemList,
	transformedNavigationMenuToApi,
	transformedNavigationMenuItemDetail,
} from '../../assets/js/transform/navigationMenu';

import { transformedMeta } from '../../assets/js/transform/meta';

export default {
	namespaced: true,
	state: {
		create: {
			isCreating: false,
		},
		edit: {
			data: {},
			isError: false,
			isLoading: false,
			isUpdating: false,
			isDeleting: false,
		},
		list: {
			isLoading: false,
			data: [],
			meta: {},
			isError: false,
		},
	},
	getters: {},
	mutations: {
		[GET_NAVIGATION_MENU_REQUEST](state) {
			state.edit.isLoading = true;
			state.edit.isError = false;
		},
		[GET_NAVIGATION_MENU_SUCCESS](state, { data }) {
			state.edit.data = transformedNavigationMenuItemDetail(data);
			state.edit.isLoading = false;
			state.edit.isError = false;
		},
		[GET_NAVIGATION_MENU_FAILURE](state) {
			state.edit.isLoading = false;
			state.edit.isError = true;
		},
		[GET_NAVIGATION_MENU_LIST_REQUEST](state, isAppend = false) {
			state.list.isLoading = true;
			if (!isAppend) {
				state.list.data = [];
				state.list.meta = {};
			}
			state.list.isError = false;
		},
		[GET_NAVIGATION_MENU_LIST_SUCCESS](state, { data = [], meta = {}, isAppend = false }) {
			state.list.isLoading = false;
			state.list.isError = false;
			const transformedData = transformedNavigationMenuItemList(data);
			state.list.data = isAppend ? [...state.list.data, ...transformedData] : transformedData;
			state.list.meta = transformedMeta(meta);
		},
		[GET_NAVIGATION_MENU_LIST_FAILURE](state) {
			state.list.data = [];
			state.list.meta = {
				total: 0,
				lastPage: 1,
				currentPage: 1,
			};
			state.list.isLoading = false;
			state.list.isError = true;
		},
		[CREATE_NAVIGATION_MENU_REQUEST](state) {
			state.create.isCreating = true;
		},
		[CREATE_NAVIGATION_MENU_SUCCESS](state) {
			state.create.isCreating = false;
		},
		[CREATE_NAVIGATION_MENU_FAILURE](state) {
			state.create.isCreating = false;
		},
		[UPDATE_NAVIGATION_MENU_REQUEST](state) {
			state.edit.isUpdating = true;
		},
		[UPDATE_NAVIGATION_MENU_SUCCESS](state) {
			state.edit.isUpdating = false;
		},
		[UPDATE_NAVIGATION_MENU_FAILURE](state) {
			state.edit.isUpdating = false;
		},
		[DELETE_NAVIGATION_MENU_REQUEST](state) {
			state.edit.isDeleting = true;
		},
		[DELETE_NAVIGATION_MENU_SUCCESS](state) {
			state.edit.isDeleting = false;
		},
		[DELETE_NAVIGATION_MENU_FAILURE](state) {
			state.edit.isDeleting = false;
		},
	},
	actions: {
		/**
		* Get navigation menus
		* @param {Object} params { page, platform, content_type }
		* @return {Array} array of navigation menu
		*/
		async getNavigationMenus({ commit }, params) {
			try {
				commit(GET_NAVIGATION_MENU_LIST_REQUEST, params.isAppend);
				const { data } = await getNavigationMenusAPI(params);
				commit(GET_NAVIGATION_MENU_LIST_SUCCESS, {
					data: data.data,
					meta: data.meta,
					isAppend: params.isAppend,
				});
			} catch (error) {
				commit(GET_NAVIGATION_MENU_LIST_FAILURE);
			}
		},
		async getNavigationMenu({ commit }, id) {
			try {
				commit(GET_NAVIGATION_MENU_REQUEST);
				const { data } = await getNavigationMenuAPI(id);
				commit(GET_NAVIGATION_MENU_SUCCESS, { data: data.data });
			} catch (error) {
				commit(GET_NAVIGATION_MENU_FAILURE);
			}
		},
		async createNavigationMenu({ commit, dispatch }, { params, contentType } = {}) {
			try {
				const newParams = {
					...params,
					contentType,
				};
				commit(CREATE_NAVIGATION_MENU_REQUEST, newParams);
				await postNavigationMenuAPI(transformedNavigationMenuToApi(newParams));
				commit(CREATE_NAVIGATION_MENU_SUCCESS);
				dispatch(
					'toast/showCreateSuccessToast',
					i18n.t('page.navigationMenu.title'),
					{ root: true },
				);
			} catch (error) {
				commit(CREATE_NAVIGATION_MENU_FAILURE);
				const message = error?.response?.data?.message ?? null;
				dispatch('toast/showCreateErrorToast', message, { root: true });
				throw error;
			}
		},
		async updateNavigationMenu({ commit, dispatch }, { params, contentType } = {}) {
			try {
				const newParams = {
					...params,
					contentType,
				};
				commit(UPDATE_NAVIGATION_MENU_REQUEST, newParams);
				await updateNavigationMenuAPI(newParams.id, transformedNavigationMenuToApi(newParams));
				commit(UPDATE_NAVIGATION_MENU_SUCCESS);
				dispatch('toast/showUpdateSuccessToast', null, { root: true });
			} catch (error) {
				commit(UPDATE_NAVIGATION_MENU_FAILURE);
				const statusCode = error?.response?.status ?? null;
				if (statusCode !== 422) {
					dispatch('toast/showUpdateErrorToast', null, { root: true });
				}
				throw error;
			}
		},
		async deleteNavigationMenu({ commit, dispatch }, id) {
			try {
				commit(DELETE_NAVIGATION_MENU_REQUEST);
				await deleteNavigationMenuAPI(id);
				commit(DELETE_NAVIGATION_MENU_SUCCESS);
				dispatch(
					'toast/showDeleteSuccessToast',
					i18n.t('page.navigationMenu.title'),
					{ root: true },
				);
			} catch (error) {
				commit(DELETE_NAVIGATION_MENU_FAILURE);
				dispatch('toast/showDeleteErrorToast', null, { root: true });
				throw error;
			}
		},
	},
};
